import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import { Paragraph, Box, TextButton, Anchor } from "@zillow/constellation-10";
import { IconChevronUpFilled, IconChevronDownFilled } from "@zillow/constellation-icons";
import API from "@zillow/mortgage-api-client";
import { yieldCallback } from "@zillow/yield-callback";
import React, { useState, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { setServerUrl, createMax, fetchRate } from "../../utils/mortgagehelpers/mortgage-helpers.esm.js";
import { commaSeparateNumber } from "../../utils/zhl-calculator.esm.js";
import LegalPopover from "../CalculatorForm/LegalPopover.esm.js";
import { useCurrentRatesButtonUrl } from "../ClosingCostCalculator/useCurrentRatesButtonUrl.esm.js";
import CheckboxField from "../Fields/CheckboxField.esm.js";
import SelectField from "../Fields/SelectField.esm.js";
import TextField from "../Fields/TextField.esm.js";
import DownPaymentField from "./DownPaymentField.esm.js";
import PropertyTaxField from "./PropertyTaxField.esm.js";
import { sendFieldInteractionEvent, legalPopoverInteractionEventBlock } from "./VALoanCalculator.Clickstream.esm.js";
import { useLastValidPayemntValues, FieldConfig, fhaCalculatorDisclaimerText, mortgageCalculatorDisclaimerText, vaCalculatorDisclaimerText } from "./VALoanCalculator.utils.esm.js";
function VALoanCalculatorFields({
  isFHAEnabled,
  isMortgageComponent
}) {
  const [advanced, setAdvanced] = useState(false);
  const clickStreamBlockId = isMortgageComponent ? "mortgage_calc" : "va_mortgage_calc";
  const legalPopoverInteractionEventCmsInfo = legalPopoverInteractionEventBlock(clickStreamBlockId);
  const form = useFormContext();
  const lastValidValues = useLastValidPayemntValues(form);
  const fieldInterestRate = form.register(FieldConfig.interestRate.name, FieldConfig.interestRate.config);
  useEffect(() => {
    setServerUrl();
    createMax();
    if (fieldInterestRate) {
      const getLatestRate = async () => {
        const rate = await fetchRate({
          homePrice: lastValidValues.fields.homePrice,
          downPayment: Math.round(lastValidValues.fields.downPayment),
          va: isFHAEnabled ? false : isMortgageComponent ? false : true,
          fha: isFHAEnabled ? true : false,
          loanProgram: lastValidValues.fields.loanProgram
        });
        form.setValue(fieldInterestRate.name, rate);
      };
      getLatestRate();
    }
  }, []);
  const currentRatesButtonUrl = useCurrentRatesButtonUrl("mortgage-rates", {
    value: lastValidValues.fields.homePrice,
    down: lastValidValues.fields.downPayment,
    auto: true,
    va: isFHAEnabled ? false : isMortgageComponent ? false : true,
    fha: isFHAEnabled ? true : false,
    zip: API.zipcode && API.zipcode.length === 5 ? API.zipcode : null,
    source: "Z_Mortgage_Calc_rates"
  });
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(TextField, { label: "Home price", name: FieldConfig.homePrice.name, leftAdornment: "$", config: FieldConfig.homePrice.config, autoComplete: "off", format: commaSeparateNumber, onChange: () => !isFHAEnabled && sendFieldInteractionEvent("Home price", "form_field", clickStreamBlockId), required: true }),
    /* @__PURE__ */ jsx(DownPaymentField, { isFHAEnabled, isMortgageComponent }),
    !isFHAEnabled && /* @__PURE__ */ jsx(TextField, { label: "ZIP code", name: FieldConfig.zipCode.name, autoComplete: "postal-code", onChange: () => {
      sendFieldInteractionEvent("ZIP code", "form_field", clickStreamBlockId);
    } }),
    /* @__PURE__ */ jsx(SelectField, { options: isFHAEnabled ? FieldConfig.fhaLoanProgram.options : FieldConfig.loanProgram.options, label: "Loan program", name: FieldConfig.loanProgram.name, config: FieldConfig.loanProgram.config, onChange: () => !isFHAEnabled && sendFieldInteractionEvent("Loan program", "form_field", clickStreamBlockId) }),
    /* @__PURE__ */ jsx(TextField, { label: "Interest rate", required: true, rightAdornment: "%", name: FieldConfig.interestRate.name, config: FieldConfig.interestRate.config, textButtonName: "See current rates", textButtonInteractionEventInfo: {
      block_type_cd: "button",
      block_id: clickStreamBlockId,
      block_displayed_txt: "See current rates"
    }, textButtonHref: currentRatesButtonUrl, autoComplete: "off", tooltip: {
      title: "Interest rate",
      body: isFHAEnabled ? /* @__PURE__ */ jsx(Paragraph, { children: "A percentage of your loan amount, like 6.25% of $400,000, that is variable based on factors you can influence and others that are market dependent." }) : /* @__PURE__ */ jsx(Paragraph, { children: "A percentage of your loan amount, like 6.25% of $400,000, that is variable based on factors you can influence and others that are market dependent." }),
      interactionEventCmsInfo: !isFHAEnabled && {
        block_type_cd: "tooltip",
        block_id: clickStreamBlockId,
        block_displayed_txt: "Interest rate"
      }
    }, onChange: () => !isFHAEnabled && sendFieldInteractionEvent("Interest rate", "form_field", clickStreamBlockId) }),
    /* @__PURE__ */ jsx(Box, { css: {
      marginBottom: "tighter"
    }, children: /* @__PURE__ */ jsxs(TextButton.Root, { type: "button", onClick: yieldCallback(() => {
      setAdvanced(!advanced);
      if (!isFHAEnabled) {
        if (advanced) {
          sendFieldInteractionEvent("Simple", "toggle", clickStreamBlockId);
        } else {
          sendFieldInteractionEvent("Advanced", "toggle", clickStreamBlockId);
        }
      }
    }), children: [
      /* @__PURE__ */ jsx(TextButton.Icon, { size: "sm", children: advanced ? /* @__PURE__ */ jsx(IconChevronUpFilled, {}) : /* @__PURE__ */ jsx(IconChevronDownFilled, {}) }),
      /* @__PURE__ */ jsx(TextButton.Label, { children: advanced ? "Simple" : "Advanced" })
    ] }) }),
    advanced && /* @__PURE__ */ jsxs(React.Fragment, { children: [
      isFHAEnabled && /* @__PURE__ */ jsx(CheckboxField, { label: "Include MIP", name: FieldConfig.includeMIP.name, tooltip: {
        title: "Include MIP",
        body: /* @__PURE__ */ jsxs(Paragraph, { children: [
          "FHA loans require two forms of mortgage insurance premiums (MIP) to protect lenders against losses that can occur when a borrower defaults on a mortgage loan. Annual MIP and Upfront MIP. This calculator will include Annual MIP.",
          " ",
          /* @__PURE__ */ jsx(Anchor, { href: "https://www.zillow.com/learn/fha-mortgage-insurance/", target: "_blank", children: "Learn more about MIP" }),
          "."
        ] })
      } }),
      isMortgageComponent && /* @__PURE__ */ jsx(CheckboxField, { label: "Include PMI", name: FieldConfig.includePMI.name, tooltip: {
        title: "Private mortgage insurance",
        body: /* @__PURE__ */ jsx(Paragraph, { children: "Mortgage insurance is required primarily for borrowers on a conventional loan with a down payment of less than 20% of the home's purchase price. It protects lenders against some or most of the losses that can occur when a borrower defaults on a mortgage loan. Contact your lender for more details." }),
        interactionEventCmsInfo: !isFHAEnabled && {
          block_type_cd: "tooltip",
          block_id: clickStreamBlockId,
          block_displayed_txt: "Include PMI"
        }
      } }),
      /* @__PURE__ */ jsx(CheckboxField, { label: "Include taxes/insurance", name: FieldConfig.includeTaxesInsurance.name, tooltip: {
        title: "Taxes and insurance",
        body: isFHAEnabled ? /* @__PURE__ */ jsx(Paragraph, { children: "Includes property taxes, homeowners insurance, mortgage insurance premium, and homeowners association dues." }) : /* @__PURE__ */ jsx(Paragraph, { children: "Includes property taxes, homeowners insurance, private mortgage insurance, and homeowners association dues." }),
        interactionEventCmsInfo: !isFHAEnabled && {
          block_type_cd: "tooltip",
          block_id: clickStreamBlockId,
          block_displayed_txt: "Include taxes/insurance"
        }
      }, onChange: () => !isFHAEnabled && sendFieldInteractionEvent("Include taxes/insurance", "toggle", clickStreamBlockId) }),
      /* @__PURE__ */ jsx(PropertyTaxField, { isFHAEnabled }),
      /* @__PURE__ */ jsx(TextField, { label: "Home insurance", required: true, leftAdornment: "$", rightAdornment: "/year", name: FieldConfig.homeInsurance.name, config: FieldConfig.homeInsurance.config, format: commaSeparateNumber, autoComplete: "off", tooltip: {
        title: "Home insurance",
        body: isFHAEnabled ? /* @__PURE__ */ jsx(Paragraph, { children: "Commonly known as hazard insurance, most lenders require insurance to provide damage protection for your home and personal property from various events, including fire, lightning, burglary, vandalism, storms, explosions, and more. The personal liability coverage in standard homeowner's insurance policies is intended to protect against lawsuits involving injuries that occur on your property. Borrowers shop for their own home insurance." }) : /* @__PURE__ */ jsx(Paragraph, { children: "Commonly known as hazard insurance, most lenders require insurance to provide damage protection for your home and personal property from a variety of events, including fire, lightning, burglary, vandalism, storms, explosions, and more. The personal liability coverage in standard homeowner's insurance policies is intended to protect against lawsuits involving injuries that occur on your property. Borrowers shop for their own home insurance. The VA calculator includes estimated home insurance premiums." }),
        interactionEventCmsInfo: !isFHAEnabled && {
          block_type_cd: "tooltip",
          block_id: clickStreamBlockId,
          block_displayed_txt: "Home insurance"
        }
      }, onChange: () => !isFHAEnabled && sendFieldInteractionEvent("Home insurance", "form_field", clickStreamBlockId) }),
      /* @__PURE__ */ jsx(TextField, { label: "HOA dues", required: true, leftAdornment: "$", rightAdornment: "/mo", name: FieldConfig.hoa.name, config: FieldConfig.hoa.config, autoComplete: "off", format: commaSeparateNumber, tooltip: {
        title: "Homeowners association dues",
        body: isFHAEnabled ? /* @__PURE__ */ jsx(Paragraph, { children: "Some condominiums or townhome communities are owned by a homeowners association (HOA), which requires owners to pay a monthly fee to cover shared amenities or services, such as garbage collection, landscaping, snow removal and pool maintenance." }) : /* @__PURE__ */ jsx(Paragraph, { children: "Typically, owners of condominiums or townhomes are required to pay homeowners association dues (known as HOA fees), to cover common amenities or services within the property such as garbage collection, landscaping, snow removal, pool maintenance, and hazard insurance." }),
        interactionEventCmsInfo: !isFHAEnabled && {
          block_type_cd: "tooltip",
          block_id: clickStreamBlockId,
          block_displayed_txt: "HOA dues"
        }
      }, onChange: () => !isFHAEnabled && sendFieldInteractionEvent("HOA dues", "form_field", clickStreamBlockId) })
    ] }),
    /* @__PURE__ */ jsx(LegalPopover, { triggerText: "Calculator disclaimer", text: isFHAEnabled ? fhaCalculatorDisclaimerText : isMortgageComponent ? mortgageCalculatorDisclaimerText : vaCalculatorDisclaimerText, interactionEventCmsInfo: !isFHAEnabled ? legalPopoverInteractionEventCmsInfo : {} })
  ] });
}
export {
  VALoanCalculatorFields as default
};
